import type { Class } from "@/types";
import { CHARACTER_CLASSES } from "@/constants";

export const assassin: Class = {
  name: "Assassin",
  id: CHARACTER_CLASSES.ASSASSIN,
  classType: CHARACTER_CLASSES.THIEF,
  description:
    "There are those who make their living dealing death from the shadows. These people are called assassins. Most are trained by secret guilds or societies; civilized lands generally forbid and destroy such organizations.",
  hitDie: "1d4",
  primaryAttribute: "dexterity",
  abilityRequirements: [
    {
      ability: "dexterity",
      min: 9,
    },
    {
      ability: "intelligence",
      min: 9,
    },
  ],
  allowedWeapons: [],
  allowedArmor: ["leather"],
  specialAbilities: [
    {
      name: "Poison",
      description:
        "Assassins learn the art of making lethal poisons. Poisons are often quite expensive to make; it is not uncommon for a single application of contact poison to cost 500 gp or more.",
    },
    {
      name: "Assassinate",
      description:
        "This is the Assassin's primary special ability. Any time an Assassin is behind an opponent in melee and it is reasonably likely the opponent doesn't know they are there, an attempt to assassinate may be made. The attack must be carried out with a one-handed piercing weapon, such as a dagger or sword. The attack is rolled at an attack bonus of +4, and if the attack hits, the victim must roll a saving throw vs. Death Ray or be instantly killed. If this saving throw is a success, the victim still suffers normal weapon damage.",
    },
    {
      name: "Waylay",
      description:
        "An Assassin can attempt to knock out an opponent in a single strike. This is performed in much the same way as the Assassinate ability, but the Assassin must be using a weapon that does subduing damage normally (i.e. a club or cudgel). The attack is rolled at a +4 attack bonus; if the Assassin hits, the victim must make a saving throw vs. Death Ray or be knocked unconscious. Creatures knocked unconscious by a Waylay attack will remain that way for 2d8 turns if not awakened.",
    },
  ],
  experienceTable: {
    1: 0,
    2: 1375,
    3: 2750,
    4: 5500,
    5: 11000,
    6: 22000,
    7: 44000,
    8: 82500,
    9: 165000,
    10: 247500,
    11: 330000,
    12: 412500,
    13: 495000,
    14: 577500,
    15: 660000,
    16: 742500,
    17: 825000,
    18: 907500,
    19: 990000,
    20: 1072500,
  },
  skills: {
    1: {
      openLocks: 15,
      pickPockets: 20,
      moveSilently: 20,
      climbWalls: 70,
      hide: 5,
      listen: 25,
      poison: 25,
    },
    2: {
      openLocks: 19,
      pickPockets: 25,
      moveSilently: 25,
      climbWalls: 72,
      hide: 10,
      listen: 29,
      poison: 30,
    },
    3: {
      openLocks: 23,
      pickPockets: 30,
      moveSilently: 30,
      climbWalls: 74,
      hide: 15,
      listen: 33,
      poison: 35,
    },
    4: {
      openLocks: 27,
      pickPockets: 35,
      moveSilently: 35,
      climbWalls: 76,
      hide: 20,
      listen: 37,
      poison: 40,
    },
    5: {
      openLocks: 31,
      pickPockets: 40,
      moveSilently: 40,
      climbWalls: 78,
      hide: 25,
      listen: 41,
      poison: 45,
    },
    6: {
      openLocks: 35,
      pickPockets: 45,
      moveSilently: 45,
      climbWalls: 80,
      hide: 30,
      listen: 45,
      poison: 50,
    },
    7: {
      openLocks: 39,
      pickPockets: 50,
      moveSilently: 50,
      climbWalls: 82,
      hide: 35,
      listen: 49,
      poison: 55,
    },
    8: {
      openLocks: 43,
      pickPockets: 55,
      moveSilently: 55,
      climbWalls: 84,
      hide: 40,
      listen: 53,
      poison: 60,
    },
    9: {
      openLocks: 47,
      pickPockets: 60,
      moveSilently: 60,
      climbWalls: 86,
      hide: 45,
      listen: 57,
      poison: 65,
    },
    10: {
      openLocks: 50,
      pickPockets: 63,
      moveSilently: 63,
      climbWalls: 87,
      hide: 48,
      listen: 60,
      poison: 69,
    },
    11: {
      openLocks: 53,
      pickPockets: 66,
      moveSilently: 66,
      climbWalls: 88,
      hide: 51,
      listen: 63,
      poison: 73,
    },
    12: {
      openLocks: 56,
      pickPockets: 69,
      moveSilently: 69,
      climbWalls: 89,
      hide: 54,
      listen: 66,
      poison: 77,
    },
    13: {
      openLocks: 59,
      pickPockets: 72,
      moveSilently: 72,
      climbWalls: 90,
      hide: 57,
      listen: 69,
      poison: 81,
    },
    14: {
      openLocks: 62,
      pickPockets: 75,
      moveSilently: 75,
      climbWalls: 91,
      hide: 60,
      listen: 72,
      poison: 85,
    },
    15: {
      openLocks: 65,
      pickPockets: 78,
      moveSilently: 78,
      climbWalls: 92,
      hide: 63,
      listen: 75,
      poison: 89,
    },
    16: {
      openLocks: 66,
      pickPockets: 79,
      moveSilently: 80,
      climbWalls: 93,
      hide: 64,
      listen: 77,
      poison: 91,
    },
    17: {
      openLocks: 67,
      pickPockets: 80,
      moveSilently: 82,
      climbWalls: 94,
      hide: 65,
      listen: 79,
      poison: 93,
    },
    18: {
      openLocks: 68,
      pickPockets: 81,
      moveSilently: 84,
      climbWalls: 95,
      hide: 66,
      listen: 81,
      poison: 95,
    },
    19: {
      openLocks: 69,
      pickPockets: 82,
      moveSilently: 86,
      climbWalls: 96,
      hide: 67,
      listen: 83,
      poison: 97,
    },
    20: {
      openLocks: 70,
      pickPockets: 83,
      moveSilently: 88,
      climbWalls: 97,
      hide: 68,
      listen: 85,
      poison: 99,
    },
  },
  supplementalContent: true,
};
